import { useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, BancontactStatus } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatDate } from "../../lib/time";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { AdminCodeDialog } from "../orders/AdminCodeDialog";

interface Props {
  /** The owner's Beheer code. */
  authorization: Authorization;
  /** The key was set or removed: Algemeen reads again whether the bank-QR confirms itself. */
  onChanged: () => void;
}

/**
 * Beheer › Algemeen › bank-QR: the shop's API key of Bancontact Pro (ADR 0024). The owner pastes it once;
 * the server shows it to Bancontact, stores it encrypted and never gives it back, so the field is always
 * empty and the card only says since when a key is active and who set it. Saving or removing it first asks
 * the special admin code from Tafo: the key decides whose account the customers pay (security review,
 * 30/10/2026).
 */
export function BancontactCard({ authorization, onChanged }: Props) {
  const { api, report, toast } = useTill();
  const text = t.general.qr.bancontact;
  const [status, setStatus] = useState<BancontactStatus | null>(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [removing, setRemoving] = useState(false);
  /** What waits for the special admin code: a new key, or null to remove the key. */
  const [waiting, setWaiting] = useState<{ apiKey: string | null } | null>(null);

  useEffect(() => {
    let current = true;
    api
      .request<BancontactStatus>("GET", "/device/bancontact", { staffToken: authorization.token, retry: true })
      .then((answer) => {
        if (current) setStatus(answer);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, authorization.token, report]);

  const send = async (apiKey: string | null, adminCode: string) => {
    setBusy(true);
    setMessage("");
    try {
      const answer = await api.request<BancontactStatus>("PUT", "/device/bancontact", {
        staffToken: authorization.token,
        body: { apiKey },
        headers: { "x-admin-code": adminCode },
      });
      setStatus(answer);
      // the key leaves the screen the moment it is stored
      setKey("");
      toast(apiKey === null ? text.removed : text.saved);
      onChanged();
    } catch (error) {
      // what Bancontact or the server says about the key or the admin code stays under the field
      if (error instanceof ApiError && ([400, 409, 502].includes(error.status) || error.code.includes("admin_code"))) {
        setMessage(error.message);
      } else {
        report(error);
      }
    } finally {
      setBusy(false);
    }
  };

  if (status === null) return null;
  return (
    <div className="gen-bancontact">
      <h4>{text.title}</h4>
      {status.active && status.setAt !== null && (
        <p className="gen-bc-on">
          <b>{text.on(formatDate(status.setAt), status.setBy ?? "")}</b>
        </p>
      )}
      <form
        className="inl gen-inl gen-bc-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (key.trim() !== "" && !busy) setWaiting({ apiKey: key });
        }}
      >
        <label>
          {text.key}{" "}
          <input
            type="password"
            value={key}
            placeholder={text.placeholder}
            autoComplete="off"
            spellCheck={false}
            maxLength={5000}
            onChange={(event) => {
              setKey(event.target.value);
              setMessage("");
            }}
          />
        </label>
        <button type="submit" className="btn primary" disabled={busy || key.trim() === ""}>
          {status.active ? text.replace : text.save}
        </button>
        {status.active && (
          <button
            type="button"
            className="btn danger"
            disabled={busy}
            onClick={() => {
              setRemoving(true);
            }}
          >
            {text.remove}
          </button>
        )}
      </form>
      {busy && <p className="note">{text.checking}</p>}
      {message !== "" && (
        <p className="pinmsg" role="alert">
          {message}
        </p>
      )}
      {status.active && <p className="note">{text.hidden}</p>}
      {removing && (
        <ConfirmDialog
          message={text.removeConfirm}
          confirm={text.remove}
          onConfirm={() => {
            setWaiting({ apiKey: null });
          }}
          onClose={() => {
            setRemoving(false);
          }}
        />
      )}
      {waiting !== null && (
        <AdminCodeDialog
          beheer={authorization}
          note={text.adminCode}
          onUnlocked={(code) => {
            setWaiting(null);
            void send(waiting.apiKey, code);
          }}
          onClose={() => {
            setWaiting(null);
          }}
        />
      )}
    </div>
  );
}
